import React, { useState } from "react";
import {
  useNotificationTemplates,
  updateNotificationTemplate,
  syncI247TemplatesFromIAT,
} from "../../util/db";

const SAMPLE_DATA = {
  eventTitle: "Finding The Money",
  eventDescription: "Transform customer interactions into life insurance sales.",
  eventLongDescription: "Learn how to transform customer interactions into a financial education opportunity that naturally leads to life insurance sales. We'll cover proven techniques for identifying opportunities during routine conversations and turning them into meaningful financial planning discussions.",
  eventTime: "Wednesday, February 25, 2026, 10:00 AM MST",
  eventTimeShort: "10:00 AM MST",
  eventLink: "https://zoom.us/j/123456789",
  recipientEmail: "agent@example.com",
};

function renderPreview(str, vars) {
  if (!str) return "";
  return str.replace(/\{\{(\w+)\}\}/g, (_, key) => vars[key] || `{{${key}}}`);
}

export default function NotificationTemplateEditor() {
  const { data: templates, status } = useNotificationTemplates();
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [syncingI247, setSyncingI247] = useState(false);
  const [syncStatus, setSyncStatus] = useState("");

  function openEdit(tpl) {
    setEditingId(tpl.id);
    setForm({
      subject: tpl.subject || "",
      htmlBody: tpl.htmlBody || "",
      textBody: tpl.textBody || "",
      smsBody: tpl.smsBody || "",
      active: tpl.active !== false,
      emailEnabled: tpl.emailEnabled !== false,
      smsEnabled: tpl.smsEnabled || false,
    });
    setPreviewMode(false);
  }

  function closeEdit() {
    setEditingId(null);
    setForm({});
    setPreviewMode(false);
  }

  async function handleSave() {
    if (!editingId) return;
    setSaving(true);
    try {
      await updateNotificationTemplate(editingId, {
        subject: form.subject,
        htmlBody: form.htmlBody,
        textBody: form.textBody,
        smsBody: form.smsBody,
        active: form.active,
        emailEnabled: form.emailEnabled,
        smsEnabled: form.smsEnabled,
      });
      closeEdit();
    } catch (err) {
      console.error("Failed to save template:", err);
      alert("Failed to save: " + (err.message || "Unknown error"));
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleActive(tpl) {
    try {
      await updateNotificationTemplate(tpl.id, { active: !tpl.active });
    } catch (err) {
      console.error("Failed to toggle template:", err);
      alert("Failed to update: " + (err.message || "Unknown error"));
    }
  }

  async function handleSyncI247() {
    setSyncingI247(true);
    setSyncStatus("");
    try {
      const result = await syncI247TemplatesFromIAT();
      setSyncStatus(
          `i247 templates synced: ${result.created} created, ` +
          `${result.updated} updated.`
      );
    } catch (err) {
      console.error("Failed to sync i247 templates:", err);
      alert("Failed to sync i247 templates: " + (err.message || "Unknown error"));
    } finally {
      setSyncingI247(false);
    }
  }

  if (status === "loading") {
    return <p className="text-sm text-gray-500 py-4">Loading templates...</p>;
  }

  if (!templates || templates.length === 0) {
    return (
      <p className="text-sm text-gray-500 py-4">
        No templates found. Run the seed script: <code className="bg-gray-100 px-1 rounded">cd functions && node seed-notification-templates.js</code>
      </p>
    );
  }

  const grouped = {
    IAT: templates.filter((t) => t.eventType === "IAT"),
    I247: templates.filter((t) => t.eventType === "I247"),
  };
  const i247NeedsSetup = grouped.I247.length < grouped.IAT.length;

  return (
    <>
      <div className="mb-4 p-3 rounded-lg border border-blue-100 bg-blue-50 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-medium text-blue-900">
            i247 Template Setup
          </p>
          <p className="text-xs text-blue-700">
            Copy all current IAT templates into i247 and keep matching notification types aligned.
          </p>
          {syncStatus && (
            <p className="text-xs text-green-700 mt-1">{syncStatus}</p>
          )}
        </div>
        <button
          onClick={handleSyncI247}
          disabled={syncingI247 || grouped.IAT.length === 0}
          className={`px-3 py-1.5 rounded text-xs font-medium ${
            i247NeedsSetup
              ? "bg-blue-600 text-white hover:bg-blue-700"
              : "bg-white text-blue-700 border border-blue-200 hover:bg-blue-100"
          } disabled:opacity-50`}
        >
          {syncingI247 ? "Syncing..." : i247NeedsSetup ? "Set Up i247 Templates" : "Resync i247 from IAT"}
        </button>
      </div>

      {Object.entries(grouped).map(([eventType, tpls]) => (
        <div key={eventType} className="mb-6">
          <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">
            {eventType} Templates
          </h4>
          <div className="space-y-1">
            {tpls.map((tpl) => (
              <div
                key={tpl.id}
                className="flex items-center justify-between p-3 rounded-lg border border-gray-200 bg-white"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`inline-block w-2 h-2 rounded-full ${tpl.active ? "bg-green-500" : "bg-gray-300"}`} />
                    <p className="text-sm font-medium text-gray-900">{tpl.name}</p>
                    {tpl.emailEnabled !== false && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-600">Email</span>
                    )}
                    {tpl.smsEnabled && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-green-50 text-green-600">SMS</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5 truncate">
                    Subject: {tpl.subject}
                  </p>
                </div>
                <div className="flex gap-2 ml-4">
                  <button
                    onClick={() => handleToggleActive(tpl)}
                    className={`text-xs px-2.5 py-1 rounded font-medium ${
                      tpl.active
                        ? "text-gray-500 hover:bg-gray-100"
                        : "text-green-600 hover:bg-green-50"
                    }`}
                  >
                    {tpl.active ? "Deactivate" : "Activate"}
                  </button>
                  <button
                    onClick={() => openEdit(tpl)}
                    className="text-xs px-2.5 py-1 rounded font-medium text-indigo-600 hover:bg-indigo-50"
                  >
                    Edit
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Edit Modal */}
      {editingId && (
        <div className="z-20 fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center">
          <div className="bg-white p-6 rounded-lg shadow-lg w-3/4 max-w-3xl relative max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Edit Template</h3>
            <button
              onClick={closeEdit}
              className="absolute top-4 right-4 text-2xl text-gray-400"
            >
              &times;
            </button>

            {!previewMode ? (
              <>
                {/* Channel Toggles */}
                <div className="flex gap-4 mb-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.emailEnabled}
                      onChange={(e) => setForm({ ...form, emailEnabled: e.target.checked })}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="text-sm text-gray-700">Email Enabled</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.smsEnabled}
                      onChange={(e) => setForm({ ...form, smsEnabled: e.target.checked })}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="text-sm text-gray-700">SMS Enabled</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.active}
                      onChange={(e) => setForm({ ...form, active: e.target.checked })}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="text-sm text-gray-700">Active</span>
                  </label>
                </div>

                {/* Subject */}
                <label className="block text-sm text-gray-500 mb-1">Subject</label>
                <input
                  type="text"
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 mb-4"
                />

                {/* HTML Body */}
                <label className="block text-sm text-gray-500 mb-1">HTML Body</label>
                <textarea
                  value={form.htmlBody}
                  onChange={(e) => setForm({ ...form, htmlBody: e.target.value })}
                  rows={10}
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 font-mono mb-4"
                />

                {/* Text Body */}
                <label className="block text-sm text-gray-500 mb-1">Plain Text Body</label>
                <textarea
                  value={form.textBody}
                  onChange={(e) => setForm({ ...form, textBody: e.target.value })}
                  rows={5}
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 font-mono mb-4"
                />

                {/* SMS Body */}
                <label className="block text-sm text-gray-500 mb-1">
                  SMS Body <span className={`ml-1 ${(form.smsBody || "").length > 160 ? "text-red-500" : "text-gray-400"}`}>({(form.smsBody || "").length}/160)</span>
                </label>
                <textarea
                  value={form.smsBody}
                  onChange={(e) => setForm({ ...form, smsBody: e.target.value })}
                  rows={3}
                  placeholder="SMS notification text (supports {{eventTitle}}, {{eventTimeShort}}, {{eventLink}})"
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 mb-4"
                />

                <p className="text-xs text-gray-400 mb-4">
                  Variables: {"{{eventTitle}}"}, {"{{eventDescription}}"}, {"{{eventLongDescription}}"}, {"{{eventTime}}"}, {"{{eventTimeShort}}"}, {"{{eventLink}}"}, {"{{recipientEmail}}"}
                </p>
              </>
            ) : (
              <>
                {/* Preview */}
                <div className="mb-4">
                  <label className="block text-sm text-gray-500 mb-1">Subject Preview</label>
                  <p className="text-sm font-medium text-gray-900 bg-gray-50 p-2 rounded">
                    {renderPreview(form.subject, SAMPLE_DATA)}
                  </p>
                </div>
                <div className="mb-4">
                  <label className="block text-sm text-gray-500 mb-1">HTML Preview</label>
                  <div
                    className="email-preview-content border rounded p-4 bg-white"
                    dangerouslySetInnerHTML={{
                      __html: renderPreview(form.htmlBody, SAMPLE_DATA),
                    }}
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm text-gray-500 mb-1">Plain Text Preview</label>
                  <pre className="text-sm text-gray-700 bg-gray-50 p-3 rounded whitespace-pre-wrap">
                    {renderPreview(form.textBody, SAMPLE_DATA)}
                  </pre>
                </div>
                {form.smsBody && (
                  <div className="mb-4">
                    <label className="block text-sm text-gray-500 mb-1">
                      SMS Preview <span className={`ml-1 ${renderPreview(form.smsBody, SAMPLE_DATA).length > 160 ? "text-red-500" : "text-gray-400"}`}>({renderPreview(form.smsBody, SAMPLE_DATA).length}/160)</span>
                    </label>
                    <div className="bg-green-50 border border-green-200 rounded p-3 text-sm text-gray-800 font-mono">
                      {renderPreview(form.smsBody, SAMPLE_DATA)}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => setPreviewMode(!previewMode)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm"
              >
                {previewMode ? "Back to Edit" : "Preview"}
              </button>
              {!previewMode && (
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 text-sm"
                >
                  {saving ? "Saving..." : "Save Template"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
